import { useEffect, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

/**
 * Whether the user has requested reduced motion.
 *
 * Native: reads `AccessibilityInfo.isReduceMotionEnabled()` and subscribes to
 * `reduceMotionChanged`. Web: reads `prefers-reduced-motion` via matchMedia.
 * Movement (lifts, entrance, content stagger) is gated on this; hover color is not.
 */
export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => {
    if (Platform.OS === 'web') {
      const mql = (globalThis as any)?.window?.matchMedia?.('(prefers-reduced-motion: reduce)');
      return !!mql?.matches;
    }
    return false;
  });

  useEffect(() => {
    let mounted = true;

    if (Platform.OS === 'web') {
      const w: any = (globalThis as any)?.window;
      const mql = w?.matchMedia?.('(prefers-reduced-motion: reduce)');
      if (!mql) return;
      setReduce(!!mql.matches);
      const onChange = (e: any) => mounted && setReduce(!!e.matches);
      // Modern browsers use addEventListener; Safari <14 only has addListener.
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange);
      } else {
        mql.addListener?.(onChange);
      }
      return () => {
        mounted = false;
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', onChange);
        } else {
          mql.removeListener?.(onChange);
        }
      };
    }

    AccessibilityInfo.isReduceMotionEnabled?.().then((v) => {
      if (mounted) setReduce(!!v);
    });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (v: boolean) =>
      mounted && setReduce(!!v),
    );
    return () => {
      mounted = false;
      sub?.remove?.();
    };
  }, []);

  return reduce;
}
